<script setup lang="ts">
import { computed } from 'vue';

import type { HotelDetailsEventSource } from '@apps/main/new-desktop/core/models';
import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';

import { StaySelectionType } from '../../../models';
import type {
  HotelDetailsFullscreenMasonryGalleryData,
  Stay,
  StayId,
} from '../../../models';
import { useHotelDetailsGalleryStore } from '../../../stores';

import HotelStayCardImage from './HotelStayCardImage.vue';
import HotelStayCardDescription from './HotelStayCardDescription.vue';
import HotelStayCardSummary from './HotelStayCardSummary.vue';

interface HotelStayCardProps {
  stay: Stay;
  source: HotelDetailsEventSource;
}
const props = defineProps<HotelStayCardProps>();

interface HotelStayCardEmits {
  (eventName: 'stay-selected', stayId: StayId): void;
  (eventName: 'more-information'): void;
  (
    eventName: 'open-gallery',
    data: HotelDetailsFullscreenMasonryGalleryData
  ): void;
}

const emit = defineEmits<HotelStayCardEmits>();

const statsigEventsStore = useStatsigEventsStore();
const hotelDetailsGalleryStore = useHotelDetailsGalleryStore();

const imageUrl = computed<string | undefined>(() => {
  if (
    props.stay.imagesURL.length > 0 &&
    props.stay.imagesURL[0] !== undefined
  ) {
    const url = hotelDetailsGalleryStore.getImageUrlWithQuality(
      props.stay.imagesURL[0],
      'small'
    );
    return url;
  }

  return undefined;
});

const imagesCount = computed<number>(() => props.stay.imagesURL.length);

function openGallery(): void {
  statsigEventsStore.logHotelDetailsRoomGalleryOpened({ source: props.source });
  emit('open-gallery', {
    index: 0,
    images: props.stay.imagesURL,
  });
}

// TODO: Move this to store
// NOTE: This is here to help content team with room codes investigation
const roomCodesString = computed<string>(() =>
  props.stay.stayId.hotelRoomIds.map((id) => id.roomCode).join(' ; ')
);
</script>

<template>
  <div
    class="hotel-stay-card"
    :data-room-code="roomCodesString"
    :class="{
      'selected-stay':
        props.stay.selectionType === StaySelectionType.ManuallySelected,
    }"
  >
    <HotelStayCardImage
      :image-url="imageUrl"
      :should-show-open-gallery-button="imagesCount > 1"
      :initial-slide="0"
      @open-gallery="openGallery"
    />
    <div class="hotel-stay-card-content">
      <HotelStayCardDescription
        class="side-margin"
        :stay="props.stay"
        :source="props.source"
        @more-information="emit('more-information')"
      />
      <div class="divider side-margin" />
      <HotelStayCardSummary
        class="side-margin"
        :stay="props.stay"
        @stay-selected="emit('stay-selected', $event)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-stay-card {
  border-radius: var(--border-radius-8);
  background-color: var(--color-white);
  padding-bottom: var(--spacing-16);
  position: relative;

  display: grid;
  grid-template-rows: min-content 1fr;
  gap: var(--spacing-12);
  border: 1px solid var(--color-secondary-90);
  overflow: hidden;

  &.selected-stay {
    border: 1px solid var(--color-black);
  }

  .hotel-stay-card-content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: var(--spacing-12);

    .divider {
      height: 1px;
      background-color: var(--color-scrollbar);
    }

    .side-margin {
      margin: 0 var(--spacing-16);
    }
  }
}
</style>
